/**
 * Strategic Performance Module - API Service
 * 
 * NOTE: This is the NEW strategic performance module.
 * API routes use: /strategic-performance/...
 * 
 * DO NOT confuse with old module at: /old-strategic-performance-settings/...
 */

import { api, publicApi } from '../../../services/api';
import {
  SPDepartment,
  SPEmployee,
  OrganizationSettings,
  StrategicPlan,
  Perspective,
  StrategicObjective,
  KPI,
  PerformanceRating,
  StrategicInitiative,
  InitiativeKPI,
  StandardInitiative,
  StandardInitiativeKPI,
  AnnualCorporateScorecard,
  AssessmentPeriod,
  AssessmentFrequency,
  ContractingPeriod,
  EmployeeAssessmentFrequency,
  CompetencySetting,
  FeedbackDimension,
  FeedbackQuestion,
  FeedbackRatingScale,
  FeedbackRatingScaleItem,
  FeedbackContributor,
  FeedbackResponse,
  ScorecardWeight,
  CEOCompetency,
  MonthlyCheckIn,
  // Employee Scorecard Types
  EmployeeScorecard,
  ScorecardPeriodStatus,
  DeptHeadStrategicInitiative,
  DeptHeadStandardInitiative,
  KeyPerformanceArea,
  EmployeeKPAInitiative,
  EmployeeIndividualInitiative,
  EmployeeCompetency,
  ScorecardNotification,
  PerformanceAssessment,
  KPIAssessment,
  DevelopmentPlan,
  Attachment,
  // DTOs
  CreateDepartmentDTO,
  UpdateDepartmentDTO,
  CreateEmployeeDTO,
  UpdateEmployeeDTO,
  CreateStrategicPlanDTO,
  UpdateStrategicPlanDTO,
  CreatePerspectiveDTO,
  UpdatePerspectiveDTO,
  CreateStrategicObjectiveDTO,
  UpdateStrategicObjectiveDTO,
  CreateKPIDTO,
  UpdateKPIDTO,
  CreatePerformanceRatingDTO,
  UpdatePerformanceRatingDTO,
  CreateStrategicInitiativeDTO,
  UpdateStrategicInitiativeDTO,
  CreateInitiativeKPIDTO,
  UpdateInitiativeKPIDTO,
  CreateStandardInitiativeDTO,
  UpdateStandardInitiativeDTO,
  CreateStandardInitiativeKPIDTO,
  UpdateStandardInitiativeKPIDTO,
  CreateAnnualCorporateScorecardDTO,
  UpdateAnnualCorporateScorecardDTO,
  CreateAssessmentPeriodDTO,
  UpdateAssessmentPeriodDTO,
  CreateAssessmentFrequencyDTO,
  UpdateAssessmentFrequencyDTO,
  CreateCompetencySettingDTO,
  UpdateCompetencySettingDTO,
  CreateFeedbackDimensionDTO,
  UpdateFeedbackDimensionDTO,
  CreateFeedbackQuestionDTO,
  UpdateFeedbackQuestionDTO,
  CreateFeedbackRatingScaleDTO,
  UpdateFeedbackRatingScaleDTO,
  CreateFeedbackRatingScaleItemDTO,
  UpdateFeedbackRatingScaleItemDTO,
  CreateFeedbackContributorDTO,
  UpdateFeedbackContributorDTO,
  CreateFeedbackResponseDTO,
  UpdateFeedbackResponseDTO,
  CreateScorecardWeightDTO,
  UpdateScorecardWeightDTO,
  CreateCEOCompetencyDTO,
  UpdateCEOCompetencyDTO,
  CreateMonthlyCheckInDTO,
  UpdateMonthlyCheckInDTO,
  // Employee Scorecard DTOs
  CreateEmployeeScorecardDTO,
  UpdateEmployeeScorecardDTO,
  CreateScorecardPeriodStatusDTO,
  UpdateScorecardPeriodStatusDTO,
  CreateDeptHeadStrategicInitiativeDTO,
  UpdateDeptHeadStrategicInitiativeDTO,
  CreateDeptHeadStandardInitiativeDTO,
  UpdateDeptHeadStandardInitiativeDTO,
  CreateKeyPerformanceAreaDTO,
  UpdateKeyPerformanceAreaDTO,
  CreateEmployeeKPAInitiativeDTO,
  UpdateEmployeeKPAInitiativeDTO,
  CreateEmployeeIndividualInitiativeDTO,
  UpdateEmployeeIndividualInitiativeDTO,
  CreateEmployeeCompetencyDTO,
  UpdateEmployeeCompetencyDTO,
  CreateScorecardNotificationDTO,
  // Assessment DTOs
  CreatePerformanceAssessmentDTO,
  UpdatePerformanceAssessmentDTO,
  CreateKPIAssessmentDTO,
  UpdateKPIAssessmentDTO,
  CreateDevelopmentPlanDTO,
  UpdateDevelopmentPlanDTO,
  ScorecardPeriodStatusType,
  PlanStatus,
  EmployeeRole,
} from '../types';

// API Base Path - Different from old module!
const API_BASE = '/strategic-performance';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Extract data from API response, handling nested data structures
 */
function extractData<T>(response: any): T {
  const backendData = response.data?.data || response.data;
  return backendData;
}

/**
 * Extract array data from API response
 */
function extractArrayData<T>(response: any): T[] {
  const backendData = response.data?.data || response.data;
  return Array.isArray(backendData) ? backendData : (backendData?.data || []);
}

/**
 * Normalize MongoDB _id to id
 */
function normalizeId<T extends { _id?: string; id?: string }>(item: T): T & { id: string } {
  return {
    ...item,
    id: item._id || item.id || '',
  };
}

/**
 * Normalize array of items with MongoDB _id
 */
function normalizeIds<T extends { _id?: string; id?: string }>(items: T[]): (T & { id: string })[] {
  return items.map(normalizeId);
}

// =============================================================================
// SETTINGS API - Departments
// =============================================================================

export const departmentsAPI = {
  getAll: async (): Promise<SPDepartment[]> => {
    const response = await api.get<any>(`${API_BASE}/settings/departments`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getById: async (id: string): Promise<SPDepartment> => {
    const response = await api.get<any>(`${API_BASE}/settings/departments/${id}`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  create: async (data: CreateDepartmentDTO): Promise<SPDepartment> => {
    const response = await api.post<any>(`${API_BASE}/settings/departments`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateDepartmentDTO): Promise<SPDepartment> => {
    const response = await api.put<any>(`${API_BASE}/settings/departments/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/settings/departments/${id}`);
  },
};

// =============================================================================
// SETTINGS API - Employees
// =============================================================================

export const employeesAPI = {
  getAll: async (filters?: { departmentId?: string; role?: EmployeeRole; status?: 'active' | 'inactive' }): Promise<SPEmployee[]> => {
    const params = new URLSearchParams();
    if (filters?.departmentId) params.append('departmentId', filters.departmentId);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    
    const url = params.toString() 
      ? `${API_BASE}/settings/employees?${params.toString()}`
      : `${API_BASE}/settings/employees`;
    
    const response = await api.get<any>(url);
    const items = extractArrayData<any>(response);
    return items.map((emp: any) => ({
      ...normalizeId(emp),
      userId: emp.userId?.MicrosoftId || emp.userId?._id || emp.userId || '',
      fullName: emp.userId?.name || emp.fullName || '',
      email: emp.userId?.email || emp.email || '',
      jobTitle: emp.userId?.jobTitle || emp.jobTitle || '',
      isAdmin: emp.userId?.isStrategicPerformanceSuperUser || false,
      departmentId: typeof emp.departmentId === 'string' 
        ? emp.departmentId 
        : (emp.departmentId?._id || emp.departmentId?.id || ''),
      department: emp.departmentId && typeof emp.departmentId === 'object' 
        ? normalizeId(emp.departmentId) 
        : undefined,
    }));
  },

  getById: async (id: string): Promise<SPEmployee> => {
    const response = await api.get<any>(`${API_BASE}/settings/employees/${id}`);
    const emp = extractData<any>(response);
    return {
      ...normalizeId(emp),
      userId: emp.userId?.MicrosoftId || emp.userId?._id || emp.userId || '',
      fullName: emp.userId?.name || emp.fullName || '',
      email: emp.userId?.email || emp.email || '',
      jobTitle: emp.userId?.jobTitle || emp.jobTitle || '',
      departmentId: typeof emp.departmentId === 'string' 
        ? emp.departmentId 
        : (emp.departmentId?._id || emp.departmentId?.id || ''),
      department: emp.departmentId && typeof emp.departmentId === 'object' 
        ? normalizeId(emp.departmentId) 
        : undefined,
    };
  },

  getByDepartment: async (departmentId: string): Promise<SPEmployee[]> => {
    return employeesAPI.getAll({ departmentId });
  },


  create: async (data: CreateEmployeeDTO): Promise<SPEmployee> => {
    const response = await api.post<any>(`${API_BASE}/settings/employees`, data);
    const emp = extractData<any>(response);
    return {
      ...normalizeId(emp),
      userId: emp.userId?.MicrosoftId || emp.userId?._id || emp.userId || '',
      fullName: emp.userId?.name || '',
      email: emp.userId?.email || '',
      jobTitle: emp.userId?.jobTitle || '',
      departmentId: typeof emp.departmentId === 'string' 
        ? emp.departmentId 
        : (emp.departmentId?._id || emp.departmentId?.id || ''),
    };
  },

  update: async (id: string, data: UpdateEmployeeDTO): Promise<SPEmployee> => {
    const response = await api.put<any>(`${API_BASE}/settings/employees/${id}`, data);
    const emp = extractData<any>(response);
    return {
      ...normalizeId(emp),
      userId: emp.userId?.MicrosoftId || emp.userId?._id || emp.userId || '',
      fullName: emp.userId?.name || '',
      email: emp.userId?.email || '',
      jobTitle: emp.userId?.jobTitle || '',
      departmentId: typeof emp.departmentId === 'string' 
        ? emp.departmentId 
        : (emp.departmentId?._id || emp.departmentId?.id || ''),
    };
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/settings/employees/${id}`);
  },

  toggleAdmin: async (id: string, isAdmin: boolean): Promise<{ isAdmin: boolean }> => {
    const response = await api.patch<any>(`${API_BASE}/settings/employees/${id}/admin`, { isAdmin });
    return extractData<any>(response);
  },

  sync: async (): Promise<{ stats: { total: number; updated: number; errors: number }; errors?: Array<{ employeeId: string; error: string }> }> => {
    const response = await api.post(`${API_BASE}/settings/employees/sync`);
    return extractData(response);
  },

  exportTemplate: async (): Promise<{ data: Array<Record<string, string>>; departments: Array<{ id: string; name: string }>; roles: string[] }> => {
    const response = await api.get(`${API_BASE}/settings/employees/export-template`);
    // Backend returns { success: true, data: [...], departments: [...], roles: [...] }
    // Axios wraps it in response.data, so response.data = { success: true, data: [...], departments: [...], roles: [...] }
    // We need to access response.data directly, not use extractData which would return just the data array
    const backendData = response.data;
    if (backendData && typeof backendData === 'object' && 'data' in backendData) {
      return {
        data: Array.isArray(backendData.data) ? backendData.data : [],
        departments: Array.isArray(backendData.departments) ? backendData.departments : [],
        roles: Array.isArray(backendData.roles) ? backendData.roles : []
      };
    }
    // Fallback: return empty structure
    return {
      data: [],
      departments: [],
      roles: []
    };
  },

  bulkImport: async (employees: Array<Record<string, string>>): Promise<{ stats: { total: number; success: number; errors: number }; results: Array<{ success: boolean; email: string; message: string }> }> => {
    const response = await api.post(`${API_BASE}/settings/employees/bulk-import`, { employees });
    return extractData(response);
  },
};

// =============================================================================
// SETTINGS API - Organization
// =============================================================================

export const organizationAPI = {
  getSettings: async (): Promise<OrganizationSettings> => {
    const response = await api.get<any>(`${API_BASE}/settings/organization`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  updateSettings: async (data: Partial<OrganizationSettings>): Promise<OrganizationSettings> => {
    const response = await api.put<any>(`${API_BASE}/settings/organization`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  uploadLogo: async (file: File): Promise<{ logoUrl: string }> => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.post<any>(`${API_BASE}/settings/organization/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return extractData(response);
  },

  deleteLogo: async (): Promise<void> => {
    await api.delete(`${API_BASE}/settings/organization/logo`);
  },
};

// =============================================================================
// STRATEGIC PLANS API
// =============================================================================

export const strategicPlansAPI = {
  getAll: async (status?: PlanStatus): Promise<StrategicPlan[]> => {
    const params = status ? `?status=${status}` : '';
    const response = await api.get<any>(`${API_BASE}/plans${params}`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getById: async (id: string): Promise<StrategicPlan> => {
    const response = await api.get<any>(`${API_BASE}/plans/${id}`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  create: async (data: CreateStrategicPlanDTO): Promise<StrategicPlan> => {
    const response = await api.post<any>(`${API_BASE}/plans`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateStrategicPlanDTO): Promise<StrategicPlan> => {
    const response = await api.put<any>(`${API_BASE}/plans/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/plans/${id}`);
  },

  duplicate: async (id: string, newName: string): Promise<StrategicPlan> => {
    const response = await api.post<any>(`${API_BASE}/plans/${id}/duplicate`, { name: newName });
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

// =============================================================================
// PERSPECTIVES API
// =============================================================================

export const perspectivesAPI = {
  getAll: async (planId: string): Promise<Perspective[]> => {
    const response = await api.get<any>(`${API_BASE}/plans/${planId}/perspectives`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getById: async (id: string): Promise<Perspective> => {
    const response = await api.get<any>(`${API_BASE}/perspectives/${id}`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  create: async (data: CreatePerspectiveDTO): Promise<Perspective> => {
    const response = await api.post<any>(`${API_BASE}/plans/${data.planId}/perspectives`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdatePerspectiveDTO): Promise<Perspective> => {
    const response = await api.put<any>(`${API_BASE}/perspectives/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/perspectives/${id}`);
  },

  reorder: async (planId: string, orderedIds: string[]): Promise<Perspective[]> => {
    const response = await api.put<any>(`${API_BASE}/plans/${planId}/perspectives/reorder`, { orderedIds });
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },
};

// =============================================================================
// STRATEGIC OBJECTIVES API
// =============================================================================

export const strategicObjectivesAPI = {
  getAll: async (planId: string, perspectiveId?: string): Promise<StrategicObjective[]> => {
    const params = perspectiveId ? `?perspectiveId=${perspectiveId}` : '';
    const response = await api.get<any>(`${API_BASE}/plans/${planId}/objectives${params}`);
    const items = extractArrayData<any>(response);
    return items.map((obj: any) => ({
      ...normalizeId(obj),
      planId: typeof obj.planId === 'string'
        ? obj.planId
        : (obj.planId?._id || obj.planId?.id || planId),
      perspectiveId: typeof obj.perspectiveId === 'string'
        ? obj.perspectiveId
        : (obj.perspectiveId?._id || obj.perspectiveId?.id || ''),
      perspective: obj.perspectiveId && typeof obj.perspectiveId === 'object'
        ? normalizeId(obj.perspectiveId)
        : undefined,
    }));
  },

  getById: async (id: string): Promise<StrategicObjective> => {
    const response = await api.get<any>(`${API_BASE}/objectives/${id}`);
    const obj = extractData<any>(response);
    return {
      ...normalizeId(obj),
      perspectiveId: typeof obj.perspectiveId === 'string'
        ? obj.perspectiveId
        : (obj.perspectiveId?._id || obj.perspectiveId?.id || ''),
      perspective: obj.perspectiveId && typeof obj.perspectiveId === 'object'
        ? normalizeId(obj.perspectiveId)
        : undefined,
    };
  },

  create: async (data: CreateStrategicObjectiveDTO): Promise<StrategicObjective> => {
    const response = await api.post<any>(`${API_BASE}/plans/${data.planId}/objectives`, data);
    const obj = extractData<any>(response);
    return normalizeId(obj);
  },

  update: async (id: string, data: UpdateStrategicObjectiveDTO): Promise<StrategicObjective> => {
    const response = await api.put<any>(`${API_BASE}/objectives/${id}`, data);
    const obj = extractData<any>(response);
    return normalizeId(obj);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/objectives/${id}`);
  },
};

// =============================================================================
// KPIs API
// =============================================================================

export const kpisAPI = {
  getAll: async (objectiveId: string): Promise<KPI[]> => {
    const response = await api.get<any>(`${API_BASE}/objectives/${objectiveId}/kpis`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items).map((kpi: any) => ({
      ...kpi,
      objectiveId: typeof kpi.objectiveId === 'string'
        ? kpi.objectiveId
        : (kpi.objectiveId?._id || kpi.objectiveId?.id || objectiveId),
    }));
  },

  getById: async (id: string): Promise<KPI> => {
    const response = await api.get<any>(`${API_BASE}/kpis/${id}`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  create: async (data: CreateKPIDTO): Promise<KPI> => {
    const response = await api.post<any>(`${API_BASE}/objectives/${data.objectiveId}/kpis`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateKPIDTO): Promise<KPI> => {
    const response = await api.put<any>(`${API_BASE}/kpis/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/kpis/${id}`);
  },
};

// =============================================================================
// PERFORMANCE RATINGS API
// =============================================================================

export const performanceRatingsAPI = {
  getAll: async (planId: string): Promise<PerformanceRating[]> => {
    const response = await api.get<any>(`${API_BASE}/plans/${planId}/ratings`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  /** Ratings for a scorecard (scorecard's copy if any, else plan's). Use in scorecard context (e.g. org performance, assessments). */
  getByScorecard: async (scorecardId: string): Promise<PerformanceRating[]> => {
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/ratings`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getById: async (id: string): Promise<PerformanceRating> => {
    const response = await api.get<any>(`${API_BASE}/ratings/${id}`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  create: async (data: CreatePerformanceRatingDTO): Promise<PerformanceRating> => {
    const response = await api.post<any>(`${API_BASE}/plans/${data.planId}/ratings`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdatePerformanceRatingDTO): Promise<PerformanceRating> => {
    const response = await api.put<any>(`${API_BASE}/ratings/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/ratings/${id}`);
  },

  createDefaultRatings: async (planId: string): Promise<PerformanceRating[]> => {
    const response = await api.post<any>(`${API_BASE}/plans/${planId}/ratings/create-defaults`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },
};

// =============================================================================
// STRATEGIC INITIATIVES API
// =============================================================================

export const strategicInitiativesAPI = {
  getAll: async (planId: string, objectiveId?: string): Promise<StrategicInitiative[]> => {
    const params = objectiveId ? `?objectiveId=${objectiveId}` : '';
    const response = await api.get<any>(`${API_BASE}/plans/${planId}/strategic-initiatives${params}`);
    const items = extractArrayData<any>(response);
    return items.map((init: any) => ({
      ...normalizeId(init),
      objectiveId: typeof init.objectiveId === 'string'
        ? init.objectiveId
        : (init.objectiveId?._id || init.objectiveId?.id || ''),
      objective: init.objectiveId && typeof init.objectiveId === 'object'
        ? normalizeId(init.objectiveId)
        : undefined,
      departmentId: typeof init.departmentId === 'string'
        ? init.departmentId
        : (init.departmentId?._id || init.departmentId?.id || undefined),
      department: init.departmentId && typeof init.departmentId === 'object'
        ? normalizeId(init.departmentId)
        : undefined,
      ownerId: typeof init.ownerId === 'string'
        ? init.ownerId
        : (init.ownerId?._id || init.ownerId?.id || undefined),
      owner: init.ownerId && typeof init.ownerId === 'object'
        ? {
            ...normalizeId(init.ownerId),
            department: init.ownerId?.departmentId && typeof init.ownerId.departmentId === 'object'
              ? normalizeId(init.ownerId.departmentId)
              : typeof init.ownerId.departmentId === 'string'
              ? { id: init.ownerId.departmentId, name: '' }
              : undefined,
          }
        : undefined,
    }));
  },

  getById: async (id: string): Promise<StrategicInitiative> => {
    const response = await api.get<any>(`${API_BASE}/strategic-initiatives/${id}`);
    const init = extractData<any>(response);
    return {
      ...normalizeId(init),
      objectiveId: typeof init.objectiveId === 'string'
        ? init.objectiveId
        : (init.objectiveId?._id || init.objectiveId?.id || ''),
      departmentId: typeof init.departmentId === 'string'
        ? init.departmentId
        : (init.departmentId?._id || init.departmentId?.id || undefined),
      department: init.departmentId && typeof init.departmentId === 'object'
        ? normalizeId(init.departmentId)
        : undefined,
      ownerId: typeof init.ownerId === 'string'
        ? init.ownerId
        : (init.ownerId?._id || init.ownerId?.id || undefined),
    };
  },

  create: async (data: CreateStrategicInitiativeDTO): Promise<StrategicInitiative> => {
    const response = await api.post<any>(`${API_BASE}/plans/${data.planId}/strategic-initiatives`, data);
    const init = extractData<any>(response);
    return {
      ...normalizeId(init),
      objectiveId: typeof init.objectiveId === 'string'
        ? init.objectiveId
        : (init.objectiveId?._id || init.objectiveId?.id || ''),
      departmentId: typeof init.departmentId === 'string'
        ? init.departmentId
        : (init.departmentId?._id || init.departmentId?.id || undefined),
      department: init.departmentId && typeof init.departmentId === 'object'
        ? normalizeId(init.departmentId)
        : undefined,
      ownerId: typeof init.ownerId === 'string'
        ? init.ownerId
        : (init.ownerId?._id || init.ownerId?.id || undefined),
    };
  },

  update: async (id: string, data: UpdateStrategicInitiativeDTO): Promise<StrategicInitiative> => {
    const response = await api.put<any>(`${API_BASE}/strategic-initiatives/${id}`, data);
    const init = extractData<any>(response);
    return {
      ...normalizeId(init),
      objectiveId: typeof init.objectiveId === 'string'
        ? init.objectiveId
        : (init.objectiveId?._id || init.objectiveId?.id || ''),
      departmentId: typeof init.departmentId === 'string'
        ? init.departmentId
        : (init.departmentId?._id || init.departmentId?.id || undefined),
      department: init.departmentId && typeof init.departmentId === 'object'
        ? normalizeId(init.departmentId)
        : undefined,
      ownerId: typeof init.ownerId === 'string'
        ? init.ownerId
        : (init.ownerId?._id || init.ownerId?.id || undefined),
    };
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/strategic-initiatives/${id}`);
  },
};

// =============================================================================
// INITIATIVE KPIs API
// =============================================================================

export const initiativeKPIsAPI = {
  getAll: async (initiativeId: string): Promise<InitiativeKPI[]> => {
    const response = await api.get<any>(`${API_BASE}/strategic-initiatives/${initiativeId}/kpis`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getById: async (id: string): Promise<InitiativeKPI> => {
    const response = await api.get<any>(`${API_BASE}/initiative-kpis/${id}`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  create: async (data: CreateInitiativeKPIDTO): Promise<InitiativeKPI> => {
    const response = await api.post<any>(`${API_BASE}/strategic-initiatives/${data.initiativeId}/kpis`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateInitiativeKPIDTO): Promise<InitiativeKPI> => {
    const response = await api.put<any>(`${API_BASE}/initiative-kpis/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/initiative-kpis/${id}`);
  },
};

// =============================================================================
// STANDARD INITIATIVES API
// =============================================================================

export const standardInitiativesAPI = {
  getAll: async (planId: string, objectiveId?: string): Promise<StandardInitiative[]> => {
    const params = objectiveId ? `?objectiveId=${objectiveId}` : '';
    const response = await api.get<any>(`${API_BASE}/plans/${planId}/standard-initiatives${params}`);
    const items = extractArrayData<any>(response);
    return items.map((init: any) => ({
      ...normalizeId(init),
      objectiveId: typeof init.objectiveId === 'string'
        ? init.objectiveId
        : (init.objectiveId?._id || init.objectiveId?.id || ''),
      objective: init.objectiveId && typeof init.objectiveId === 'object'
        ? normalizeId(init.objectiveId)
        : undefined,
      departmentId: typeof init.departmentId === 'string'
        ? init.departmentId
        : (init.departmentId?._id || init.departmentId?.id || undefined),
      department: init.departmentId && typeof init.departmentId === 'object'
        ? normalizeId(init.departmentId)
        : undefined,
      ownerId: typeof init.ownerId === 'string'
        ? init.ownerId
        : (init.ownerId?._id || init.ownerId?.id || undefined),
      owner: init.ownerId && typeof init.ownerId === 'object'
        ? {
            ...normalizeId(init.ownerId),
            department: init.ownerId?.departmentId && typeof init.ownerId.departmentId === 'object'
              ? normalizeId(init.ownerId.departmentId)
              : typeof init.ownerId.departmentId === 'string'
              ? { id: init.ownerId.departmentId, name: '' }
              : undefined,
          }
        : undefined,
    }));
  },

  getById: async (id: string): Promise<StandardInitiative> => {
    const response = await api.get<any>(`${API_BASE}/standard-initiatives/${id}`);
    const init = extractData<any>(response);
    return {
      ...normalizeId(init),
      objectiveId: typeof init.objectiveId === 'string'
        ? init.objectiveId
        : (init.objectiveId?._id || init.objectiveId?.id || ''),
      departmentId: typeof init.departmentId === 'string'
        ? init.departmentId
        : (init.departmentId?._id || init.departmentId?.id || undefined),
      ownerId: typeof init.ownerId === 'string'
        ? init.ownerId
        : (init.ownerId?._id || init.ownerId?.id || undefined),
    };
  },

  create: async (data: CreateStandardInitiativeDTO): Promise<StandardInitiative> => {
    const response = await api.post<any>(`${API_BASE}/plans/${data.planId}/standard-initiatives`, data);
    const init = extractData<any>(response);
    return normalizeId(init);
  },

  update: async (id: string, data: UpdateStandardInitiativeDTO): Promise<StandardInitiative> => {
    const response = await api.put<any>(`${API_BASE}/standard-initiatives/${id}`, data);
    const init = extractData<any>(response);
    return normalizeId(init);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/standard-initiatives/${id}`);
  },
};

// =============================================================================
// STANDARD INITIATIVE KPIs API
// =============================================================================

export const standardInitiativeKPIsAPI = {
  getAll: async (initiativeId: string): Promise<StandardInitiativeKPI[]> => {
    const response = await api.get<any>(`${API_BASE}/standard-initiatives/${initiativeId}/kpis`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getById: async (id: string): Promise<StandardInitiativeKPI> => {
    const response = await api.get<any>(`${API_BASE}/standard-initiative-kpis/${id}`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  create: async (data: CreateStandardInitiativeKPIDTO): Promise<StandardInitiativeKPI> => {
    const response = await api.post<any>(`${API_BASE}/standard-initiatives/${data.initiativeId}/kpis`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateStandardInitiativeKPIDTO): Promise<StandardInitiativeKPI> => {
    const response = await api.put<any>(`${API_BASE}/standard-initiative-kpis/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/standard-initiative-kpis/${id}`);
  },
};

// =============================================================================
// ANNUAL CORPORATE SCORECARDS API
// =============================================================================

export const scorecardsAPI = {
  getAll: async (planId?: string): Promise<AnnualCorporateScorecard[]> => {
    const params = planId ? `?planId=${planId}` : '';
    const response = await api.get<any>(`${API_BASE}/scorecards${params}`);
    const items = extractArrayData<any>(response);
    return items.map((sc: any) => ({
      ...normalizeId(sc),
      planId: typeof sc.planId === 'string'
        ? sc.planId
        : (sc.planId?._id || sc.planId?.id || ''),
      plan: sc.planId && typeof sc.planId === 'object'
        ? normalizeId(sc.planId)
        : undefined,
    }));
  },

  getById: async (id: string): Promise<AnnualCorporateScorecard> => {
    const response = await api.get<any>(`${API_BASE}/scorecards/${id}`);
    const sc = extractData<any>(response);
    return {
      ...normalizeId(sc),
      planId: typeof sc.planId === 'string'
        ? sc.planId
        : (sc.planId?._id || sc.planId?.id || ''),
      plan: sc.planId && typeof sc.planId === 'object'
        ? normalizeId(sc.planId)
        : undefined,
    };
  },

  create: async (data: CreateAnnualCorporateScorecardDTO): Promise<AnnualCorporateScorecard> => {
    const response = await api.post<any>(`${API_BASE}/scorecards`, data);
    const sc = extractData<any>(response);
    return normalizeId(sc);
  },

  update: async (id: string, data: UpdateAnnualCorporateScorecardDTO): Promise<AnnualCorporateScorecard> => {
    const response = await api.put<any>(`${API_BASE}/scorecards/${id}`, data);
    const sc = extractData<any>(response);
    return normalizeId(sc);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/scorecards/${id}`);
  },
};

// =============================================================================
// ASSESSMENT PERIODS API
// =============================================================================

export const assessmentPeriodsAPI = {
  getAll: async (scorecardId: string): Promise<AssessmentPeriod[]> => {
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/assessment-periods`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getById: async (id: string): Promise<AssessmentPeriod> => {
    const response = await api.get<any>(`${API_BASE}/assessment-periods/${id}`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  create: async (data: CreateAssessmentPeriodDTO): Promise<AssessmentPeriod> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${data.scorecardId}/assessment-periods`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateAssessmentPeriodDTO): Promise<AssessmentPeriod> => {
    const response = await api.put<any>(`${API_BASE}/assessment-periods/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/assessment-periods/${id}`);
  },

  generatePeriods: async (scorecardId: string): Promise<AssessmentPeriod[]> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/assessment-periods/generate`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  /** Send assessment-period-open emails to employees with the given frequency */
  notify: async (scorecardId: string, frequency: 'Quarterly' | 'Bi-Annually' | 'Annually', periodNumber: number): Promise<{ sent: number; failed: number; total: number; message: string }> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/assessment-periods/notify`, { frequency, periodNumber });
    const data = response?.data?.data ?? response?.data;
    return {
      sent: data?.sent ?? 0,
      failed: data?.failed ?? 0,
      total: data?.total ?? 0,
      message: data?.message ?? response?.data?.message ?? 'Done',
    };
  },
};

// =============================================================================
// CONTRACTING PERIODS API
// =============================================================================

export const contractingPeriodsAPI = {
  getAll: async (scorecardId: string): Promise<ContractingPeriod[]> => {
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/contracting-periods`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getById: async (id: string): Promise<ContractingPeriod> => {
    const response = await api.get<any>(`${API_BASE}/contracting-periods/${id}`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  create: async (scorecardId: string, data: { frequency: AssessmentFrequency; startDate: string; endDate: string }): Promise<ContractingPeriod> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/contracting-periods`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: Partial<ContractingPeriod>): Promise<ContractingPeriod> => {
    const response = await api.put<any>(`${API_BASE}/contracting-periods/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  /** Send contracting-period-open emails to employees with the given frequency */
  notify: async (scorecardId: string, frequency: 'Quarterly' | 'Bi-Annually' | 'Annually'): Promise<{ sent: number; failed: number; total: number; message: string }> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/contracting-periods/notify`, { frequency });
    const data = response?.data?.data ?? response?.data;
    return {
      sent: data?.sent ?? 0,
      failed: data?.failed ?? 0,
      total: data?.total ?? 0,
      message: data?.message ?? response?.data?.message ?? 'Done',
    };
  },
};

// =============================================================================
// ASSESSMENT FREQUENCIES API
// =============================================================================

export const assessmentFrequenciesAPI = {
  getAll: async (scorecardId: string): Promise<EmployeeAssessmentFrequency[]> => {
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/assessment-frequencies`);
    const items = extractArrayData<any>(response);
    return items.map((item: any) => ({
      ...normalizeId(item),
      employeeId: typeof item.employeeId === 'string'
        ? item.employeeId
        : (item.employeeId?._id || item.employeeId?.id || ''),
      employee: item.employeeId && typeof item.employeeId === 'object'
        ? {
            ...normalizeId(item.employeeId),
            userId: item.employeeId.userId?.MicrosoftId || item.employeeId.userId?._id || item.employeeId.userId || '',
            fullName: item.employeeId.userId?.name || '',
            email: item.employeeId.userId?.email || '',
            jobTitle: item.employeeId.userId?.jobTitle || '',
            departmentId: typeof item.employeeId.departmentId === 'string'
              ? item.employeeId.departmentId
              : (item.employeeId.departmentId?._id || item.employeeId.departmentId?.id || ''),
          }
        : undefined,
    }));
  },

  createOrUpdate: async (scorecardId: string, data: CreateAssessmentFrequencyDTO): Promise<EmployeeAssessmentFrequency> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/assessment-frequencies`, data);
    const item = extractData<any>(response);
    return {
      ...normalizeId(item),
      employeeId: typeof item.employeeId === 'string'
        ? item.employeeId
        : (item.employeeId?._id || item.employeeId?.id || ''),
      employee: item.employeeId && typeof item.employeeId === 'object'
        ? {
            ...normalizeId(item.employeeId),
            userId: item.employeeId.userId?.MicrosoftId || item.employeeId.userId?._id || item.employeeId.userId || '',
            fullName: item.employeeId.userId?.name || '',
            email: item.employeeId.userId?.email || '',
            jobTitle: item.employeeId.userId?.jobTitle || '',
            departmentId: typeof item.employeeId.departmentId === 'string'
              ? item.employeeId.departmentId
              : (item.employeeId.departmentId?._id || item.employeeId.departmentId?.id || ''),
          }
        : undefined,
    };
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/assessment-frequencies/${id}`);
  },
};

// =============================================================================
// COMPETENCY SETTINGS API
// =============================================================================

export const competencySettingsAPI = {
  getAll: async (scorecardId: string): Promise<CompetencySetting[]> => {
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/competency-settings`);
    const items = extractArrayData<any>(response);
    return items.map((item: any) => ({
      ...normalizeId(item),
      employeeId: typeof item.employeeId === 'string'
        ? item.employeeId
        : (item.employeeId?._id || item.employeeId?.id || ''),
      employee: item.employeeId && typeof item.employeeId === 'object'
        ? {
            ...normalizeId(item.employeeId),
            userId: item.employeeId.userId?.MicrosoftId || item.employeeId.userId?._id || item.employeeId.userId || '',
            fullName: item.employeeId.userId?.name || '',
            email: item.employeeId.userId?.email || '',
            jobTitle: item.employeeId.userId?.jobTitle || '',
            departmentId: typeof item.employeeId.departmentId === 'string'
              ? item.employeeId.departmentId
              : (item.employeeId.departmentId?._id || item.employeeId.departmentId?.id || ''),
          }
        : undefined,
    }));
  },

  createOrUpdate: async (scorecardId: string, data: CreateCompetencySettingDTO): Promise<CompetencySetting> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/competency-settings`, data);
    const item = extractData<any>(response);
    return {
      ...normalizeId(item),
      employeeId: typeof item.employeeId === 'string'
        ? item.employeeId
        : (item.employeeId?._id || item.employeeId?.id || ''),
      employee: item.employeeId && typeof item.employeeId === 'object'
        ? {
            ...normalizeId(item.employeeId),
            userId: item.employeeId.userId?.MicrosoftId || item.employeeId.userId?._id || item.employeeId.userId || '',
            fullName: item.employeeId.userId?.name || '',
            email: item.employeeId.userId?.email || '',
            jobTitle: item.employeeId.userId?.jobTitle || '',
            departmentId: typeof item.employeeId.departmentId === 'string'
              ? item.employeeId.departmentId
              : (item.employeeId.departmentId?._id || item.employeeId.departmentId?.id || ''),
          }
        : undefined,
    };
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/competency-settings/${id}`);
  },
};

// =============================================================================
// FEEDBACK DIMENSIONS API
// =============================================================================

export const feedbackDimensionsAPI = {
  getAll: async (scorecardId: string, role?: EmployeeRole): Promise<FeedbackDimension[]> => {
    const params = role ? `?role=${role}` : '';
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/feedback-dimensions${params}`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getContribution: async (scorecardId: string, role?: EmployeeRole): Promise<{ contribution: number; dimensions: FeedbackDimension[] }> => {
    const params = role ? `?role=${role}` : '';
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/feedback-dimensions/contribution${params}`);
    const data = extractData<any>(response);
    return {
      contribution: data.contribution || 0,
      dimensions: (data.dimensions || []).map((item: any) => normalizeId(item)),
    };
  },

  create: async (scorecardId: string, data: CreateFeedbackDimensionDTO): Promise<FeedbackDimension> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/feedback-dimensions`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateFeedbackDimensionDTO): Promise<FeedbackDimension> => {
    const response = await api.put<any>(`${API_BASE}/feedback-dimensions/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/feedback-dimensions/${id}`);
  },

  updateContribution: async (scorecardId: string, role: EmployeeRole, contributionPercentage: number): Promise<FeedbackDimension> => {
    const response = await api.put<any>(`${API_BASE}/scorecards/${scorecardId}/feedback-dimensions/contribution`, {
      role,
      contributionPercentage,
    });
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  updateWeight: async (id: string, weight: number): Promise<FeedbackDimension> => {
    const response = await api.put<any>(`${API_BASE}/feedback-dimensions/${id}`, { weight });
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

// =============================================================================
// FEEDBACK QUESTIONS API
// =============================================================================

export const feedbackQuestionsAPI = {
  getAll: async (dimensionId: string): Promise<FeedbackQuestion[]> => {
    const response = await api.get<any>(`${API_BASE}/feedback-dimensions/${dimensionId}/questions`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  create: async (dimensionId: string, data: CreateFeedbackQuestionDTO): Promise<FeedbackQuestion> => {
    const response = await api.post<any>(`${API_BASE}/feedback-dimensions/${dimensionId}/questions`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateFeedbackQuestionDTO): Promise<FeedbackQuestion> => {
    const response = await api.put<any>(`${API_BASE}/feedback-questions/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/feedback-questions/${id}`);
  },
};

// =============================================================================
// FEEDBACK RATING SCALES API
// =============================================================================

export const feedbackRatingScalesAPI = {
  getAll: async (scorecardId: string, role?: EmployeeRole): Promise<FeedbackRatingScale[]> => {
    const params = role ? `?role=${role}` : '';
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/feedback-rating-scales${params}`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  createOrUpdate: async (scorecardId: string, data: CreateFeedbackRatingScaleDTO): Promise<FeedbackRatingScale> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/feedback-rating-scales`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  updateEnabled: async (scorecardId: string, role: EmployeeRole, enabled: boolean): Promise<FeedbackRatingScale> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/feedback-rating-scales`, {
      role,
      enabled,
    });
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

// =============================================================================
// FEEDBACK RATING SCALE ITEMS API
// =============================================================================

export const feedbackRatingScaleItemsAPI = {
  getAll: async (scaleId: string): Promise<FeedbackRatingScaleItem[]> => {
    const response = await api.get<any>(`${API_BASE}/feedback-rating-scales/${scaleId}/items`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  create: async (scaleId: string, data: CreateFeedbackRatingScaleItemDTO): Promise<FeedbackRatingScaleItem> => {
    const response = await api.post<any>(`${API_BASE}/feedback-rating-scales/${scaleId}/items`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateFeedbackRatingScaleItemDTO): Promise<FeedbackRatingScaleItem> => {
    const response = await api.put<any>(`${API_BASE}/feedback-rating-scale-items/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/feedback-rating-scale-items/${id}`);
  },
};

// =============================================================================
// FEEDBACK CONTRIBUTORS API
// =============================================================================

export const feedbackContributorsAPI = {
  getByAssessment: async (performanceAssessmentId: string): Promise<FeedbackContributor[]> => {
    const response = await api.get<any>(`${API_BASE}/performance-assessments/${performanceAssessmentId}/feedback-contributors`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  create: async (performanceAssessmentId: string, data: CreateFeedbackContributorDTO): Promise<FeedbackContributor> => {
    const response = await api.post<any>(`${API_BASE}/performance-assessments/${performanceAssessmentId}/feedback-contributors`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateFeedbackContributorDTO): Promise<FeedbackContributor> => {
    const response = await api.put<any>(`${API_BASE}/feedback-contributors/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/feedback-contributors/${id}`);
  },

  sendInvite: async (id: string): Promise<void> => {
    await api.post(`${API_BASE}/feedback-contributors/${id}/send-invite`);
  },

  sendReminder: async (id: string): Promise<void> => {
    await api.post(`${API_BASE}/feedback-contributors/${id}/send-reminder`);
  },

  // Public endpoints (no auth token required)
  getByToken: async (token: string): Promise<FeedbackContributor> => {
    const response = await publicApi.get<any>(`${API_BASE}/feedback-contributors/by-token/${token}`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  verify: async (id: string, email: string): Promise<FeedbackContributor> => {
    const response = await publicApi.post<any>(`${API_BASE}/feedback-contributors/${id}/verify`, { email });
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  submitResponses: async (id: string, responses: { questionId: string; ratingValue: number }[]): Promise<void> => {
    await publicApi.post(`${API_BASE}/feedback-contributors/${id}/responses`, { responses });
  },

  // Public endpoints for fetching dimensions, questions, and rating scale by token
  getDimensionsByToken: async (token: string): Promise<FeedbackDimension[]> => {
    const response = await publicApi.get<any>(`${API_BASE}/feedback-contributors/by-token/${token}/dimensions`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getQuestionsByToken: async (token: string): Promise<FeedbackQuestion[]> => {
    const response = await publicApi.get<any>(`${API_BASE}/feedback-contributors/by-token/${token}/questions`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getRatingScaleByToken: async (token: string): Promise<FeedbackRatingScale | null> => {
    const response = await publicApi.get<any>(`${API_BASE}/feedback-contributors/by-token/${token}/rating-scale`);
    const item = extractData<any>(response);
    return item ? normalizeId(item) : null;
  },
};

// =============================================================================
// FEEDBACK RESPONSES API
// =============================================================================

export const feedbackResponsesAPI = {
  getByContributors: async (contributorIds: string[]): Promise<FeedbackResponse[]> => {
    if (contributorIds.length === 0) return [];
    const response = await api.post<any>(`${API_BASE}/feedback-responses/by-contributors`, { contributorIds });
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  create: async (data: CreateFeedbackResponseDTO): Promise<FeedbackResponse> => {
    const response = await api.post<any>(`${API_BASE}/feedback-responses`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  bulkCreate: async (data: CreateFeedbackResponseDTO[]): Promise<FeedbackResponse[]> => {
    const response = await api.post<any>(`${API_BASE}/feedback-responses/bulk`, { responses: data });
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  update: async (id: string, data: UpdateFeedbackResponseDTO): Promise<FeedbackResponse> => {
    const response = await api.put<any>(`${API_BASE}/feedback-responses/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/feedback-responses/${id}`);
  },
};

// =============================================================================
// SCORECARD WEIGHTS API
// =============================================================================

export const scorecardWeightsAPI = {
  getAll: async (scorecardId: string, role?: EmployeeRole): Promise<ScorecardWeight[]> => {
    const params = role ? `?role=${role}` : '';
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/weights${params}`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  createOrUpdate: async (scorecardId: string, data: CreateScorecardWeightDTO): Promise<ScorecardWeight> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/weights`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  bulkSave: async (scorecardId: string, role: EmployeeRole, weights: Array<{ section: string; weight: number }>): Promise<ScorecardWeight[]> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/weights/bulk`, { role, weights });
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },
};

// =============================================================================
// CEO COMPETENCIES API
// =============================================================================

export const ceoCompetenciesAPI = {
  getAll: async (scorecardId: string): Promise<CEOCompetency[]> => {
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/ceo-competencies`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  create: async (scorecardId: string, data: CreateCEOCompetencyDTO): Promise<CEOCompetency> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/ceo-competencies`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateCEOCompetencyDTO): Promise<CEOCompetency> => {
    const response = await api.put<any>(`${API_BASE}/ceo-competencies/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/ceo-competencies/${id}`);
  },
};

// =============================================================================
// MONTHLY CHECK-INS API
// =============================================================================

export const monthlyCheckInsAPI = {
  getAll: async (scorecardId: string): Promise<MonthlyCheckIn[]> => {
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/monthly-check-ins`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  createOrUpdate: async (scorecardId: string, data: CreateMonthlyCheckInDTO): Promise<MonthlyCheckIn> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/monthly-check-ins`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (scorecardId: string, monthNumber: number, data: UpdateMonthlyCheckInDTO): Promise<MonthlyCheckIn> => {
    const response = await api.put<any>(`${API_BASE}/scorecards/${scorecardId}/monthly-check-ins/${monthNumber}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  /** Send monthly check-in open emails to employees assigned to this scorecard */
  notify: async (scorecardId: string, monthNumber: number): Promise<{ sent: number; failed: number; total: number; message: string }> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/monthly-check-ins/notify`, { monthNumber });
    const data = response?.data?.data ?? response?.data;
    return {
      sent: data?.sent ?? 0,
      failed: data?.failed ?? 0,
      total: data?.total ?? 0,
      message: data?.message ?? response?.data?.message ?? 'Done',
    };
  },
};

// =============================================================================
// PROGRESS UPDATES API
// =============================================================================

export const progressUpdatesAPI = {
  getByEmployeeScorecard: async (employeeScorecardId: string, monthNumber?: number): Promise<any[]> => {
    const params = monthNumber ? `?monthNumber=${monthNumber}` : '';
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/progress-updates${params}`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  createOrUpdate: async (employeeScorecardId: string, data: {
    monthNumber: number;
    kpiId: string;
    kpiType: string;
    kpiName: string;
    progressUpdate: string;
  }): Promise<any> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/progress-updates`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  bulkCreateOrUpdate: async (employeeScorecardId: string, monthNumber: number, updates: Array<{
    kpiId: string;
    kpiType: string;
    kpiName: string;
    progressUpdate: string;
  }>): Promise<any[]> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/progress-updates/bulk`, {
      monthNumber,
      updates,
    });
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },
};

// =============================================================================
// CHECK-IN SUBMISSIONS API
// =============================================================================

export const checkInSubmissionsAPI = {
  getByEmployeeScorecard: async (employeeScorecardId: string, monthNumber?: number): Promise<any[]> => {
    const params = monthNumber ? `?monthNumber=${monthNumber}` : '';
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/check-in-submissions${params}`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  create: async (employeeScorecardId: string, data: { monthNumber: number }): Promise<any> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/check-in-submissions`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: { status?: 'submitted' | 'on_schedule' | 'behind_schedule'; reviewerComments?: string }): Promise<any> => {
    const response = await api.put<any>(`${API_BASE}/check-in-submissions/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  getTeamSubmissions: async (scorecardId: string, monthNumber: number): Promise<any[]> => {
    const response = await api.get<any>(`${API_BASE}/check-in-submissions/team?scorecardId=${scorecardId}&monthNumber=${monthNumber}`);
    const items = extractArrayData<any>(response);
    const normalized = normalizeIds(items);
    // Explicitly normalize employeeScorecardId and reviewedBy if they're populated objects
    return normalized.map(item => ({
      ...item,
      employeeScorecardId: String(item.employeeScorecardId?._id || item.employeeScorecardId?.id || item.employeeScorecardId || ''),
      reviewedBy: item.reviewedBy ? (item.reviewedBy._id || item.reviewedBy.id || item.reviewedBy) : null,
    }));
  },
};

// =============================================================================
// SETTINGS API - Power BI
// =============================================================================

export interface PowerBIConfig {
  id: string;
  enabled: boolean;
  api_key: string | null;
  description: string | null;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

export const powerBIAPI = {
  getConfig: async (): Promise<PowerBIConfig | null> => {
    try {
      const response = await api.get<any>(`${API_BASE}/settings/powerbi`);
      const item = extractData<any>(response);
      return item ? normalizeId(item) : null;
    } catch (error: any) {
      // If 404, return null (config doesn't exist yet)
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  upsertConfig: async (data: Partial<PowerBIConfig>): Promise<PowerBIConfig> => {
    const response = await api.post<any>(`${API_BASE}/settings/powerbi`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

// =============================================================================
// COMBINED EXPORT
// =============================================================================

export const strategicPerformanceAPI = {
  departments: departmentsAPI,
  employees: employeesAPI,
  organization: organizationAPI,
  powerBI: powerBIAPI,
  strategicPlans: strategicPlansAPI,
  perspectives: perspectivesAPI,
  strategicObjectives: strategicObjectivesAPI,
  kpis: kpisAPI,
  performanceRatings: performanceRatingsAPI,
  strategicInitiatives: strategicInitiativesAPI,
  initiativeKPIs: initiativeKPIsAPI,
  standardInitiatives: standardInitiativesAPI,
  standardInitiativeKPIs: standardInitiativeKPIsAPI,
  scorecards: scorecardsAPI,
  assessmentPeriods: assessmentPeriodsAPI,
  contractingPeriods: contractingPeriodsAPI,
  assessmentFrequencies: assessmentFrequenciesAPI,
  competencySettings: competencySettingsAPI,
  feedbackDimensions: feedbackDimensionsAPI,
  feedbackQuestions: feedbackQuestionsAPI,
  feedbackRatingScales: feedbackRatingScalesAPI,
  feedbackRatingScaleItems: feedbackRatingScaleItemsAPI,
  feedbackContributors: feedbackContributorsAPI,
  feedbackResponses: feedbackResponsesAPI,
  scorecardWeights: scorecardWeightsAPI,
  ceoCompetencies: ceoCompetenciesAPI,
  monthlyCheckIns: monthlyCheckInsAPI,
  progressUpdates: progressUpdatesAPI,
  checkInSubmissions: checkInSubmissionsAPI,
};

// =============================================================================
// CALIBRATION COMMITTEE API
// =============================================================================

export interface CalibrationCommitteeMember {
  id: string;
  scorecardId: string;
  employeeId: string | { id: string; fullName: string; jobTitle: string; role: string; department?: { name: string } };
  isAdmin: boolean;
  addedAt: string;
  addedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const calibrationCommitteeAPI = {
  getAll: async (scorecardId?: string): Promise<CalibrationCommitteeMember[]> => {
    const url = scorecardId 
      ? `${API_BASE}/calibration-committee?scorecardId=${scorecardId}`
      : `${API_BASE}/calibration-committee`;
    const response = await api.get<any>(url);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getByScorecard: async (scorecardId: string): Promise<CalibrationCommitteeMember[]> => {
    const response = await api.get<any>(`${API_BASE}/calibration-committee?scorecardId=${scorecardId}`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items).map((item: any) => ({
      ...item,
      employeeId: item.employeeId && typeof item.employeeId === 'object' 
        ? item.employeeId 
        : item.employeeId,
    }));
  },

  addMember: async (scorecardId: string, employeeId: string, isAdmin = false): Promise<CalibrationCommitteeMember> => {
    const response = await api.post<any>(`${API_BASE}/calibration-committee`, {
      scorecardId,
      employeeId,
      isAdmin,
    });
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  removeMember: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/calibration-committee/${id}`);
  },

  updateMember: async (id: string, data: { isAdmin?: boolean }): Promise<CalibrationCommitteeMember> => {
    const response = await api.put<any>(`${API_BASE}/calibration-committee/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

// =============================================================================
// CALIBRATION SESSIONS API
// =============================================================================

export interface CalibrationSession {
  id: string;
  scorecardId: string;
  status: 'open' | 'closed';
  openedAt?: string;
  openedBy?: string;
  openedByEmployee?: { fullName: string };
  closedAt?: string;
  closedBy?: string;
  closedByEmployee?: { fullName: string };
  createdAt?: string;
  updatedAt?: string;
}

export const calibrationSessionsAPI = {
  getByScorecard: async (scorecardId: string): Promise<CalibrationSession | null> => {
    try {
      const response = await api.get<any>(`${API_BASE}/calibration-sessions?scorecardId=${scorecardId}`);
      const item = extractData<any>(response);
      return item ? normalizeId(item) : null;
    } catch (error: any) {
      // If API doesn't exist (404), return null silently
      // The interceptor will suppress the error log for calibration APIs
      if (error?.response?.status === 404) {
        return null;
      }
      // Re-throw other errors
      throw error;
    }
  },

  open: async (scorecardId: string, employeeId: string, existingSessionId?: string): Promise<CalibrationSession> => {
    try {
      if (existingSessionId) {
        // Update existing session
        const response = await api.put<any>(`${API_BASE}/calibration-sessions/${existingSessionId}`, {
          status: 'open',
          openedAt: new Date().toISOString(),
          openedBy: employeeId,
          closedAt: null,
          closedBy: null,
        });
        const item = extractData<any>(response);
        return normalizeId(item);
      } else {
        // Create new session
        const response = await api.post<any>(`${API_BASE}/calibration-sessions`, {
          scorecardId,
          status: 'open',
          openedAt: new Date().toISOString(),
          openedBy: employeeId,
        });
        const item = extractData<any>(response);
        return normalizeId(item);
      }
    } catch (error: any) {
      // If API doesn't exist (404), throw a more specific error
      if (error?.response?.status === 404) {
        const notImplementedError: any = new Error('Calibration session API not yet implemented');
        notImplementedError.response = { status: 404 };
        throw notImplementedError;
      }
      throw error;
    }
  },

  close: async (sessionId: string, employeeId: string): Promise<CalibrationSession> => {
    try {
      const response = await api.put<any>(`${API_BASE}/calibration-sessions/${sessionId}`, {
        status: 'closed',
        closedAt: new Date().toISOString(),
        closedBy: employeeId,
      });
      const item = extractData<any>(response);
      return normalizeId(item);
    } catch (error: any) {
      // If API doesn't exist (404), throw a more specific error
      if (error?.response?.status === 404) {
        const notImplementedError: any = new Error('Calibration session API not yet implemented');
        notImplementedError.response = { status: 404 };
        throw notImplementedError;
      }
      throw error;
    }
  },

  notifyOpened: async (scorecardId: string, openedByEmployeeId: string): Promise<{ sent: number; message: string }> => {
    const response = await api.post<any>(`${API_BASE}/calibration-sessions/notify-opened`, { scorecardId, openedByEmployeeId });
    const data = response?.data?.data ?? response?.data;
    return { sent: data?.sent ?? 0, message: response?.data?.message ?? '' };
  },
  notifyClosed: async (scorecardId: string, closedByEmployeeId: string): Promise<{ sent: number; message: string }> => {
    const response = await api.post<any>(`${API_BASE}/calibration-sessions/notify-closed`, { scorecardId, closedByEmployeeId });
    const data = response?.data?.data ?? response?.data;
    return { sent: data?.sent ?? 0, message: response?.data?.message ?? '' };
  },
};

// =============================================================================
// ALIGNMENT COMMENTS API
// =============================================================================

export interface AlignmentComment {
  id: string;
  alignmentSessionId: string;
  employeeScorecardId: string;
  kpiId: string;
  kpiType?: string | null;
  comment: string;
  commenterId: string;
  commenterName: string;
  resolved: boolean;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolvedByName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const alignmentCommentsAPI = {
  getBySession: async (alignmentSessionId: string, employeeScorecardId?: string, kpiId?: string): Promise<AlignmentComment[]> => {
    try {
      const params = new URLSearchParams();
      params.append('alignmentSessionId', alignmentSessionId);
      if (employeeScorecardId) {
        params.append('employeeScorecardId', employeeScorecardId);
      }
      if (kpiId) {
        params.append('kpiId', kpiId);
      }
      const response = await api.get<any>(`${API_BASE}/alignment-comments?${params.toString()}`);
      const items = extractArrayData<any>(response);
      return normalizeIds(items);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  create: async (data: {
    alignmentSessionId: string;
    employeeScorecardId: string;
    kpiId: string;
    kpiType?: string;
    comment: string;
    commenterId: string;
  }): Promise<AlignmentComment> => {
    const response = await api.post<any>(`${API_BASE}/alignment-comments`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: {
    resolved?: boolean;
    resolvedBy?: string;
  }): Promise<AlignmentComment> => {
    const response = await api.put<any>(`${API_BASE}/alignment-comments/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/alignment-comments/${id}`);
  },
};

// =============================================================================
// ALIGNMENT SESSIONS API
// =============================================================================

export interface AlignmentSession {
  id: string;
  scorecardId: string;
  status: 'open' | 'closed';
  openedAt?: string;
  openedBy?: string;
  openedByEmployee?: { fullName: string };
  closedAt?: string;
  closedBy?: string;
  closedByEmployee?: { fullName: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface StrategicExecutionComment {
  id: string;
  scorecardId: string;
  quarter: string;
  kpiId: string;
  comment: string;
  progressPercent: number;
  createdAt?: string;
  updatedAt?: string;
}

export const strategicExecutionCommentsAPI = {
  getByScorecardAndQuarter: async (scorecardId: string, quarter: string): Promise<StrategicExecutionComment[]> => {
    try {
      const response = await api.get<any>(`${API_BASE}/strategic-execution-comments?scorecardId=${scorecardId}&quarter=${quarter}`);
      const items = extractArrayData<any>(response);
      return normalizeIds(items);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  upsert: async (data: {
    scorecardId: string;
    quarter: string;
    kpiId: string;
    comment?: string;
    progressPercent?: number;
  }): Promise<StrategicExecutionComment> => {
    const response = await api.post<any>(`${API_BASE}/strategic-execution-comments`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

export interface StrategicInitiativeExecutionComment {
  id: string;
  scorecardId: string;
  quarter: string;
  kpiId: string;
  initiativeId: string;
  departmentId: string;
  comment: string;
  progressPercent: number;
  createdAt?: string;
  updatedAt?: string;
}

export const strategicInitiativeExecutionCommentsAPI = {
  getByScorecardAndQuarter: async (scorecardId: string, quarter: string): Promise<StrategicInitiativeExecutionComment[]> => {
    try {
      const response = await api.get<any>(`${API_BASE}/strategic-initiative-execution-comments?scorecardId=${scorecardId}&quarter=${quarter}`);
      const items = extractArrayData<any>(response);
      return normalizeIds(items);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  upsert: async (data: {
    scorecardId: string;
    quarter: string;
    kpiId: string;
    initiativeId: string;
    departmentId: string;
    comment?: string;
    progressPercent?: number;
  }): Promise<StrategicInitiativeExecutionComment> => {
    const response = await api.post<any>(`${API_BASE}/strategic-initiative-execution-comments`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

export type QuarterCode = 'Q1' | 'Q2' | 'Q3' | 'Q4';

export interface ScorecardKPITarget {
  id: string;
  scorecardId: string;
  kpiId: string;
  targetQ1?: string | null;
  targetQ2?: string | null;
  targetQ3?: string | null;
  targetQ4?: string | null;
  /** Quarters this KPI applies to; weight is distributed only over these. */
  applicableQuarters?: QuarterCode[];
  createdAt?: string;
  updatedAt?: string;
}

export const scorecardKPITargetsAPI = {
  getByScorecard: async (scorecardId: string): Promise<ScorecardKPITarget[]> => {
    try {
      const response = await api.get<any>(`${API_BASE}/scorecard-kpi-targets?scorecardId=${scorecardId}`);
      const items = extractArrayData<any>(response);
      return normalizeIds(items);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  upsert: async (data: {
    scorecardId: string;
    kpiId: string;
    targetQ1?: string;
    targetQ2?: string;
    targetQ3?: string;
    targetQ4?: string;
    applicableQuarters?: QuarterCode[];
  }): Promise<ScorecardKPITarget> => {
    const response = await api.post<any>(`${API_BASE}/scorecard-kpi-targets`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

// Per-scorecard visibility controls for objectives and KPIs
export const scorecardVisibilityAPI = {
  hideObjective: async (scorecardId: string, objectiveId: string): Promise<AnnualCorporateScorecard> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/hidden-objectives`, {
      objectiveId,
    });
    const sc = extractData<any>(response);
    return normalizeId(sc);
  },

  hideKpi: async (scorecardId: string, kpiId: string): Promise<AnnualCorporateScorecard> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/hidden-kpis`, {
      kpiId,
    });
    const sc = extractData<any>(response);
    return normalizeId(sc);
  },

  hideInitiative: async (scorecardId: string, initiativeId: string): Promise<AnnualCorporateScorecard> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/hidden-initiatives`, {
      initiativeId,
    });
    const sc = extractData<any>(response);
    return normalizeId(sc);
  },

  hideInitiativeKpi: async (scorecardId: string, initiativeKpiId: string): Promise<AnnualCorporateScorecard> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/hidden-initiative-kpis`, {
      initiativeKpiId,
    });
    const sc = extractData<any>(response);
    return normalizeId(sc);
  },

  hideStandardInitiative: async (scorecardId: string, initiativeId: string): Promise<AnnualCorporateScorecard> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/hidden-standard-initiatives`, {
      initiativeId,
    });
    const sc = extractData<any>(response);
    return normalizeId(sc);
  },

  hideStandardInitiativeKpi: async (scorecardId: string, standardInitiativeKpiId: string): Promise<AnnualCorporateScorecard> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/hidden-standard-initiative-kpis`, {
      standardInitiativeKpiId,
    });
    const sc = extractData<any>(response);
    return normalizeId(sc);
  },
};

export interface ScorecardInitiativeKPITarget {
  id: string;
  scorecardId: string;
  initiativeKpiId: string;
  targetQ1?: string | null;
  targetQ2?: string | null;
  targetQ3?: string | null;
  targetQ4?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const scorecardInitiativeKPITargetsAPI = {
  getByScorecard: async (scorecardId: string): Promise<ScorecardInitiativeKPITarget[]> => {
    try {
      const response = await api.get<any>(`${API_BASE}/scorecard-initiative-kpi-targets?scorecardId=${scorecardId}`);
      const items = extractArrayData<any>(response);
      return normalizeIds(items);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },
  upsert: async (data: {
    scorecardId: string;
    initiativeKpiId: string;
    targetQ1?: string;
    targetQ2?: string;
    targetQ3?: string;
    targetQ4?: string;
  }): Promise<ScorecardInitiativeKPITarget> => {
    const response = await api.post<any>(`${API_BASE}/scorecard-initiative-kpi-targets`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

export interface ScorecardStandardInitiativeKPITarget {
  id: string;
  scorecardId: string;
  standardInitiativeKpiId: string;
  targetQ1?: string | null;
  targetQ2?: string | null;
  targetQ3?: string | null;
  targetQ4?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const scorecardStandardInitiativeKPITargetsAPI = {
  getByScorecard: async (scorecardId: string): Promise<ScorecardStandardInitiativeKPITarget[]> => {
    try {
      const response = await api.get<any>(`${API_BASE}/scorecard-standard-initiative-kpi-targets?scorecardId=${scorecardId}`);
      const items = extractArrayData<any>(response);
      return normalizeIds(items);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },
  upsert: async (data: {
    scorecardId: string;
    standardInitiativeKpiId: string;
    targetQ1?: string;
    targetQ2?: string;
    targetQ3?: string;
    targetQ4?: string;
  }): Promise<ScorecardStandardInitiativeKPITarget> => {
    const response = await api.post<any>(`${API_BASE}/scorecard-standard-initiative-kpi-targets`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

export const alignmentSessionsAPI = {
  getByScorecard: async (scorecardId: string): Promise<AlignmentSession | null> => {
    try {
      const response = await api.get<any>(`${API_BASE}/alignment-sessions?scorecardId=${scorecardId}`);
      const item = extractData<any>(response);
      return item ? normalizeId(item) : null;
    } catch (error: any) {
      // If API doesn't exist (404), return null silently
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  open: async (scorecardId: string, employeeId: string, existingSessionId?: string): Promise<AlignmentSession> => {
    try {
      if (existingSessionId) {
        const response = await api.put<any>(`${API_BASE}/alignment-sessions/${existingSessionId}`, {
          status: 'open',
          openedAt: new Date().toISOString(),
          openedBy: employeeId,
          closedAt: null,
          closedBy: null,
        });
        const item = extractData<any>(response);
        return normalizeId(item);
      } else {
        const response = await api.post<any>(`${API_BASE}/alignment-sessions`, {
          scorecardId,
          status: 'open',
          openedAt: new Date().toISOString(),
          openedBy: employeeId,
        });
        const item = extractData<any>(response);
        return normalizeId(item);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        const notImplementedError: any = new Error('Alignment session API not yet implemented');
        notImplementedError.response = { status: 404 };
        throw notImplementedError;
      }
      throw error;
    }
  },

  close: async (sessionId: string, employeeId: string): Promise<AlignmentSession> => {
    try {
      const response = await api.put<any>(`${API_BASE}/alignment-sessions/${sessionId}`, {
        status: 'closed',
        closedAt: new Date().toISOString(),
        closedBy: employeeId,
      });
      const item = extractData<any>(response);
      return normalizeId(item);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        const notImplementedError: any = new Error('Alignment session API not yet implemented');
        notImplementedError.response = { status: 404 };
        throw notImplementedError;
      }
      throw error;
    }
  },

  notifyOpened: async (scorecardId: string, openedByEmployeeId: string): Promise<{ sent: number; message: string }> => {
    const response = await api.post<any>(`${API_BASE}/alignment-sessions/notify-opened`, { scorecardId, openedByEmployeeId });
    const data = response?.data?.data ?? response?.data;
    return { sent: data?.sent ?? 0, message: response?.data?.message ?? '' };
  },
  notifyClosed: async (scorecardId: string, closedByEmployeeId: string): Promise<{ sent: number; message: string }> => {
    const response = await api.post<any>(`${API_BASE}/alignment-sessions/notify-closed`, { scorecardId, closedByEmployeeId });
    const data = response?.data?.data ?? response?.data;
    return { sent: data?.sent ?? 0, message: response?.data?.message ?? '' };
  },
};

// =============================================================================
// CALIBRATION ADJUSTMENTS API
// =============================================================================

export interface CalibrationAdjustment {
  id: string;
  kpiAssessmentId: string;
  calibrationSessionId: string;
  originalRatingScaleId: string | null;
  adjustedRatingScaleId: string;
  comments: string;
  adjustedBy?: string;
  adjustedByEmployee?: { fullName: string };
  adjustedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const calibrationAdjustmentsAPI = {
  getBySession: async (sessionId: string): Promise<CalibrationAdjustment[]> => {
    try {
      const response = await api.get<any>(`${API_BASE}/calibration-adjustments?sessionId=${sessionId}`);
      const items = extractArrayData<any>(response);
      // Map backend field names to frontend field names
      return normalizeIds(items).map((item: any) => ({
        ...item,
        originalRatingScaleId: item.originalRatingId || item.originalRatingScaleId || null,
        adjustedRatingScaleId: item.adjustedRatingId || item.adjustedRatingScaleId || '',
      }));
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  create: async (data: {
    kpiAssessmentId: string;
    calibrationSessionId: string;
    originalRatingScaleId: string | null;
    adjustedRatingScaleId: string;
    comments: string;
    adjustedBy: string;
  }): Promise<CalibrationAdjustment> => {
    // Map frontend field names to backend field names
    const backendData = {
      kpiAssessmentId: data.kpiAssessmentId,
      calibrationSessionId: data.calibrationSessionId,
      originalRatingId: data.originalRatingScaleId,
      adjustedRatingId: data.adjustedRatingScaleId,
      comments: data.comments,
      adjustedBy: data.adjustedBy,
    };
    const response = await api.post<any>(`${API_BASE}/calibration-adjustments`, backendData);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    // Map backend field names to frontend field names
    return {
      ...normalized,
      originalRatingScaleId: normalized.originalRatingId || normalized.originalRatingScaleId || null,
      adjustedRatingScaleId: normalized.adjustedRatingId || normalized.adjustedRatingScaleId || '',
    };
  },

  update: async (id: string, data: {
    adjustedRatingScaleId?: string;
    comments?: string;
    adjustedBy?: string;
  }): Promise<CalibrationAdjustment> => {
    // Map frontend field names to backend field names
    const backendData: any = {};
    if (data.adjustedRatingScaleId !== undefined) {
      backendData.adjustedRatingId = data.adjustedRatingScaleId;
    }
    if (data.comments !== undefined) {
      backendData.comments = data.comments;
    }
    if (data.adjustedBy !== undefined) {
      backendData.adjustedBy = data.adjustedBy;
    }
    const response = await api.put<any>(`${API_BASE}/calibration-adjustments/${id}`, backendData);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    // Map backend field names to frontend field names
    return {
      ...normalized,
      originalRatingScaleId: normalized.originalRatingId || normalized.originalRatingScaleId || null,
      adjustedRatingScaleId: normalized.adjustedRatingId || normalized.adjustedRatingScaleId || '',
    };
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/calibration-adjustments/${id}`);
  },
};

// =============================================================================
// SHAREPOINT API
// =============================================================================

export const sharePointAPI = {
  getConfig: async (): Promise<any> => {
    const response = await api.get<any>(`${API_BASE}/settings/sharepoint`);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  upsertConfig: async (data: Partial<any>): Promise<any> => {
    const response = await api.put<any>(`${API_BASE}/settings/sharepoint`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  toggleEnabled: async (enabled: boolean): Promise<any> => {
    const response = await api.patch<any>(`${API_BASE}/settings/sharepoint/enabled`, { enabled });
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  getSyncStats: async (): Promise<any> => {
    const response = await api.get<any>(`${API_BASE}/settings/sharepoint/stats`);
    return extractData(response);
  },

  sync: async (): Promise<{ message?: string; error?: string; hint?: string; synced?: number; failed?: number; last_sync_at?: string; errors?: string[] }> => {
    try {
      const response = await api.post<any>(`${API_BASE}/settings/sharepoint/sync`);
      // Backend returns: { success: true, message: "...", data: { synced, failed, last_sync_at, errors } }
      // We need to return both message and data
      const data = extractData(response) || {};
      const message = response.data?.message;
      return {
        message,
        ...(typeof data === 'object' && data !== null ? data : {}),
      };
    } catch (error: any) {
      // Handle error responses
      const errorData = error?.response?.data;
      return {
        error: errorData?.error || error?.message || 'Sync failed',
        hint: errorData?.hint,
      };
    }
  },
};

// =============================================================================
// ORGANIZATION DEVELOPMENT TEAM API
// =============================================================================

export interface OrgDevTeamMember {
  id: string;
  userId: string;
  userMicrosoftId: string;
  fullName: string;
  email: string;
  jobTitle: string;
  role: string;
  department: string;
  addedAt: string;
}

export const orgDevTeamAPI = {
  getAll: async (): Promise<OrgDevTeamMember[]> => {
    const response = await api.get(`${API_BASE}/settings/org-dev-team`);
    const items = extractArrayData<OrgDevTeamMember>(response);
    return normalizeIds(items);
  },

  addMembers: async (userIds: string[]): Promise<{ data: OrgDevTeamMember[]; message?: string; errors?: any[]; skipped?: any[] }> => {
    const response = await api.post(`${API_BASE}/settings/org-dev-team`, { userIds });
    // Backend returns: { success: true, data: [...], message: "...", ... }
    // axios wraps it in response.data, so response.data = { success: true, data: [...], message: "..." }
    const responseData = response.data || {};
    return {
      data: normalizeIds((responseData.data || []) as OrgDevTeamMember[]),
      message: responseData.message,
      errors: responseData.errors,
      skipped: responseData.skipped,
    };
  },

  removeMember: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/settings/org-dev-team/${id}`);
  },
};

// =============================================================================
// AUDIT TRAIL API
// =============================================================================

export interface AuditLog {
  id: string;
  table_name: string;
  record_id: string;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  old_data: Record<string, any> | null;
  new_data: Record<string, any> | null;
  changed_fields: string[] | null;
  employee_id: string | null;
  employee_name: string | null;
  employee_email: string | null;
  created_at: string;
}

export interface AuditTrailResponse {
  data: AuditLog[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const auditTrailAPI = {
  getAll: async (params?: {
    tableName?: string;
    action?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }): Promise<AuditTrailResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.tableName) queryParams.append('tableName', params.tableName);
    if (params?.action) queryParams.append('action', params.action);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await api.get(`${API_BASE}/settings/audit-trail?${queryParams.toString()}`);
    // Backend returns: { success: true, data: [...], pagination: {...} }
    // We need to extract both data and pagination
    const responseData = response.data;
    return {
      data: responseData?.data || [],
      pagination: responseData?.pagination || {
        total: 0,
        page: 1,
        limit: 50,
        totalPages: 0,
      },
    };
  },
};

// =============================================================================
// EMPLOYEE SCORECARDS API (My Scorecard)
// =============================================================================

export const employeeScorecardsAPI = {
  getAll: async (employeeId?: string): Promise<EmployeeScorecard[]> => {
    const params = employeeId ? `?employeeId=${employeeId}` : '';
    const response = await api.get<any>(`${API_BASE}/employee-scorecards${params}`);
    const items = extractArrayData<any>(response);
    // Normalize IDs and ensure scorecardId/employeeId/supervisorId are strings
    return normalizeIds(items).map(item => ({
      ...item,
      scorecardId: String(item.scorecardId?._id || item.scorecardId?.id || item.scorecardId || ''),
      employeeId: String(item.employeeId?._id || item.employeeId?.id || item.employeeId || ''),
      supervisorId: item.supervisorId ? String(item.supervisorId?._id || item.supervisorId?.id || item.supervisorId || '') : undefined,
    }));
  },

  getById: async (id: string): Promise<EmployeeScorecard> => {
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${id}`);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      scorecardId: String(normalized.scorecardId?._id || normalized.scorecardId?.id || normalized.scorecardId || ''),
      employeeId: String(normalized.employeeId?._id || normalized.employeeId?.id || normalized.employeeId || ''),
      supervisorId: normalized.supervisorId ? String(normalized.supervisorId?._id || normalized.supervisorId?.id || normalized.supervisorId || '') : undefined,
    };
  },

  create: async (data: CreateEmployeeScorecardDTO): Promise<EmployeeScorecard> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      scorecardId: String(normalized.scorecardId?._id || normalized.scorecardId?.id || normalized.scorecardId || ''),
      employeeId: String(normalized.employeeId?._id || normalized.employeeId?.id || normalized.employeeId || ''),
      supervisorId: normalized.supervisorId ? String(normalized.supervisorId?._id || normalized.supervisorId?.id || normalized.supervisorId || '') : undefined,
    };
  },

  update: async (id: string, data: UpdateEmployeeScorecardDTO): Promise<EmployeeScorecard> => {
    const response = await api.put<any>(`${API_BASE}/employee-scorecards/${id}`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      scorecardId: String(normalized.scorecardId?._id || normalized.scorecardId?.id || normalized.scorecardId || ''),
      employeeId: String(normalized.employeeId?._id || normalized.employeeId?.id || normalized.employeeId || ''),
      supervisorId: normalized.supervisorId ? String(normalized.supervisorId?._id || normalized.supervisorId?.id || normalized.supervisorId || '') : undefined,
    };
  },
};

// =============================================================================
// SCORECARD PERIOD STATUS API
// =============================================================================

/**
 * Helper to normalize period status - ensures employeeScorecardId, supervisorId, and approvedBy are strings
 */
function normalizePeriodStatus(item: any): ScorecardPeriodStatus {
  const normalized = normalizeId(item);
  return {
    ...normalized,
    employeeScorecardId: String(item.employeeScorecardId?._id || item.employeeScorecardId?.id || item.employeeScorecardId || ''),
    supervisorId: item.supervisorId ? String(item.supervisorId?._id || item.supervisorId?.id || item.supervisorId || '') : undefined,
    approvedBy: item.approvedBy ? String(item.approvedBy?._id || item.approvedBy?.id || item.approvedBy || '') : undefined,
  };
}

export const periodStatusAPI = {
  getByScorecard: async (employeeScorecardId: string): Promise<ScorecardPeriodStatus[]> => {
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/period-statuses`);
    const items = extractArrayData<any>(response);
    return items.map(normalizePeriodStatus);
  },

  getAll: async (params?: { status?: ScorecardPeriodStatusType; supervisorId?: string }): Promise<ScorecardPeriodStatus[]> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.supervisorId) queryParams.append('supervisorId', params.supervisorId);
    const response = await api.get<any>(`${API_BASE}/period-statuses?${queryParams.toString()}`);
    const items = extractArrayData<any>(response);
    return items.map(normalizePeriodStatus);
  },

  create: async (employeeScorecardId: string, data: CreateScorecardPeriodStatusDTO): Promise<ScorecardPeriodStatus> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/period-statuses`, data);
    const item = extractData<any>(response);
    return normalizePeriodStatus(item);
  },

  update: async (id: string, data: UpdateScorecardPeriodStatusDTO): Promise<ScorecardPeriodStatus> => {
    const response = await api.put<any>(`${API_BASE}/period-statuses/${id}`, data);
    const item = extractData<any>(response);
    return normalizePeriodStatus(item);
  },

  sendReminder: async (id: string): Promise<void> => {
    await api.post(`${API_BASE}/period-statuses/${id}/send-reminder`);
  },
};

// =============================================================================
// DEPARTMENT HEAD STRATEGIC INITIATIVES API
// =============================================================================

export const deptHeadStrategicInitiativesAPI = {
  getByScorecard: async (employeeScorecardId: string): Promise<DeptHeadStrategicInitiative[]> => {
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/dept-head-strategic-initiatives`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items).map(item => {
      // Preserve _id values in nested kpiWeights array (critical for custom KPIs)
      const kpiWeights = (item.kpiWeights || []).map((kw: any) => {
        // Preserve _id as both _id and id for compatibility
        const kwId = kw._id ? (typeof kw._id === 'object' ? kw._id.toString() : String(kw._id)) : (kw.id || undefined);
        return {
          ...kw,
          _id: kwId,
          id: kwId || kw.id, // Keep id for backward compatibility
        };
      });
      
      return {
      ...item,
      initiativeId: String(item.initiativeId?._id || item.initiativeId?.id || item.initiativeId || ''),
      employeeScorecardId: String(item.employeeScorecardId?._id || item.employeeScorecardId?.id || item.employeeScorecardId || ''),
        kpiWeights,
      };
    });
  },

  create: async (employeeScorecardId: string, data: CreateDeptHeadStrategicInitiativeDTO): Promise<DeptHeadStrategicInitiative> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/dept-head-strategic-initiatives`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      initiativeId: String(normalized.initiativeId?._id || normalized.initiativeId?.id || normalized.initiativeId || ''),
      employeeScorecardId: String(normalized.employeeScorecardId?._id || normalized.employeeScorecardId?.id || normalized.employeeScorecardId || ''),
    };
  },

  update: async (id: string, data: UpdateDeptHeadStrategicInitiativeDTO): Promise<DeptHeadStrategicInitiative> => {
    const response = await api.put<any>(`${API_BASE}/dept-head-strategic-initiatives/${id}`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      initiativeId: String(normalized.initiativeId?._id || normalized.initiativeId?.id || normalized.initiativeId || ''),
      employeeScorecardId: String(normalized.employeeScorecardId?._id || normalized.employeeScorecardId?.id || normalized.employeeScorecardId || ''),
    };
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/dept-head-strategic-initiatives/${id}`);
  },
};

// =============================================================================
// DEPARTMENT HEAD STANDARD INITIATIVES API
// =============================================================================

export const deptHeadStandardInitiativesAPI = {
  getByScorecard: async (employeeScorecardId: string): Promise<DeptHeadStandardInitiative[]> => {
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/dept-head-standard-initiatives`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items).map(item => ({
      ...item,
      initiativeId: String(item.initiativeId?._id || item.initiativeId?.id || item.initiativeId || ''),
      employeeScorecardId: String(item.employeeScorecardId?._id || item.employeeScorecardId?.id || item.employeeScorecardId || ''),
    }));
  },

  create: async (employeeScorecardId: string, data: CreateDeptHeadStandardInitiativeDTO): Promise<DeptHeadStandardInitiative> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/dept-head-standard-initiatives`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      initiativeId: String(normalized.initiativeId?._id || normalized.initiativeId?.id || normalized.initiativeId || ''),
      employeeScorecardId: String(normalized.employeeScorecardId?._id || normalized.employeeScorecardId?.id || normalized.employeeScorecardId || ''),
    };
  },

  update: async (id: string, data: UpdateDeptHeadStandardInitiativeDTO): Promise<DeptHeadStandardInitiative> => {
    const response = await api.put<any>(`${API_BASE}/dept-head-standard-initiatives/${id}`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      initiativeId: String(normalized.initiativeId?._id || normalized.initiativeId?.id || normalized.initiativeId || ''),
      employeeScorecardId: String(normalized.employeeScorecardId?._id || normalized.employeeScorecardId?.id || normalized.employeeScorecardId || ''),
    };
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/dept-head-standard-initiatives/${id}`);
  },
};

// =============================================================================
// KEY PERFORMANCE AREAS (KPA) API
// =============================================================================

export const keyPerformanceAreasAPI = {
  getByScorecard: async (employeeScorecardId: string): Promise<KeyPerformanceArea[]> => {
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/key-performance-areas`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items).map(item => ({
      ...item,
      objectiveId: String(item.objectiveId?._id || item.objectiveId?.id || item.objectiveId || ''),
      employeeScorecardId: String(item.employeeScorecardId?._id || item.employeeScorecardId?.id || item.employeeScorecardId || ''),
    }));
  },

  create: async (employeeScorecardId: string, data: CreateKeyPerformanceAreaDTO): Promise<KeyPerformanceArea> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/key-performance-areas`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      objectiveId: String(item.objectiveId?._id || item.objectiveId?.id || item.objectiveId || ''),
      employeeScorecardId: String(item.employeeScorecardId?._id || item.employeeScorecardId?.id || item.employeeScorecardId || ''),
    };
  },

  update: async (id: string, data: UpdateKeyPerformanceAreaDTO): Promise<KeyPerformanceArea> => {
    const response = await api.put<any>(`${API_BASE}/key-performance-areas/${id}`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      objectiveId: String(item.objectiveId?._id || item.objectiveId?.id || item.objectiveId || ''),
      employeeScorecardId: String(item.employeeScorecardId?._id || item.employeeScorecardId?.id || item.employeeScorecardId || ''),
    };
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/key-performance-areas/${id}`);
  },
};

// =============================================================================
// EMPLOYEE KPA INITIATIVES API
// =============================================================================

export const employeeKPAInitiativesAPI = {
  getByScorecard: async (employeeScorecardId: string): Promise<EmployeeKPAInitiative[]> => {
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/kpa-initiatives`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  create: async (employeeScorecardId: string, data: CreateEmployeeKPAInitiativeDTO): Promise<EmployeeKPAInitiative> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/kpa-initiatives`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateEmployeeKPAInitiativeDTO): Promise<EmployeeKPAInitiative> => {
    const response = await api.put<any>(`${API_BASE}/kpa-initiatives/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/kpa-initiatives/${id}`);
  },
};

// =============================================================================
// APPROVED DEPARTMENT HEAD INITIATIVES API (for cascading)
// =============================================================================

export const approvedDeptHeadInitiativesAPI = {
  get: async (departmentId: string, scorecardId: string): Promise<{
    strategic: any[];
    standard: any[];
    kpa: any[];
    deptHeadScorecardId: string | null;
  }> => {
    const response = await api.get<any>(`${API_BASE}/approved-dept-head-initiatives`, {
      params: { departmentId, scorecardId }
    });
    const data = extractData<any>(response);
    return {
      strategic: normalizeIds(data.strategic || []),
      standard: normalizeIds(data.standard || []),
      kpa: normalizeIds(data.kpa || []),
      deptHeadScorecardId: data.deptHeadScorecardId || null
    };
  },
};

// =============================================================================
// EMPLOYEE INDIVIDUAL INITIATIVES API
// =============================================================================

export const employeeIndividualInitiativesAPI = {
  getByScorecard: async (employeeScorecardId: string): Promise<EmployeeIndividualInitiative[]> => {
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/individual-initiatives`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items).map(item => {
      let objective: any = undefined;
      let objectiveId = '';
      
      if (item.objectiveId) {
        if (typeof item.objectiveId === 'object' && item.objectiveId !== null) {
          // Objective is populated
          objectiveId = String(item.objectiveId._id || item.objectiveId.id || '');
          objective = {
            ...normalizeId(item.objectiveId),
            perspectiveId: item.objectiveId.perspectiveId 
              ? (typeof item.objectiveId.perspectiveId === 'object' 
                  ? normalizeId(item.objectiveId.perspectiveId)
                  : item.objectiveId.perspectiveId)
              : undefined,
          };
        } else {
          objectiveId = String(item.objectiveId);
        }
      }
      
      return {
        ...item,
        objectiveId,
        objective,
      };
    });
  },

  create: async (employeeScorecardId: string, data: CreateEmployeeIndividualInitiativeDTO): Promise<EmployeeIndividualInitiative> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/individual-initiatives`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateEmployeeIndividualInitiativeDTO): Promise<EmployeeIndividualInitiative> => {
    const response = await api.put<any>(`${API_BASE}/individual-initiatives/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/individual-initiatives/${id}`);
  },
};

// =============================================================================
// EMPLOYEE COMPETENCIES API
// =============================================================================

export const employeeCompetenciesAPI = {
  getByScorecard: async (employeeScorecardId: string): Promise<EmployeeCompetency[]> => {
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/competencies`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  create: async (employeeScorecardId: string, data: CreateEmployeeCompetencyDTO): Promise<EmployeeCompetency> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/competencies`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateEmployeeCompetencyDTO): Promise<EmployeeCompetency> => {
    const response = await api.put<any>(`${API_BASE}/employee-competencies/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/employee-competencies/${id}`);
  },
};

// =============================================================================
// SCORECARD NOTIFICATIONS API
// =============================================================================

export const scorecardNotificationsAPI = {
  getByScorecard: async (employeeScorecardId: string): Promise<ScorecardNotification[]> => {
    const response = await api.get<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/notifications`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  create: async (employeeScorecardId: string, data: CreateScorecardNotificationDTO): Promise<ScorecardNotification> => {
    const response = await api.post<any>(`${API_BASE}/employee-scorecards/${employeeScorecardId}/notifications`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },
};

// =============================================================================
// PERFORMANCE ASSESSMENTS API (My Assessment)
// =============================================================================

export const performanceAssessmentsAPI = {
  getAll: async (params?: { 
    employeeId?: string; 
    employeeScorecardId?: string;
    status?: string;
    supervisorId?: string;
  }): Promise<PerformanceAssessment[]> => {
    const queryParams = new URLSearchParams();
    if (params?.employeeId) queryParams.append('employeeId', params.employeeId);
    if (params?.employeeScorecardId) queryParams.append('employeeScorecardId', params.employeeScorecardId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.supervisorId) queryParams.append('supervisorId', params.supervisorId);
    const response = await api.get<any>(`${API_BASE}/performance-assessments?${queryParams.toString()}`);
    const items = extractArrayData<any>(response);
    // Normalize IDs and ensure employeeScorecardId, employeeId, supervisorId are strings
    return normalizeIds(items).map(item => ({
      ...item,
      employeeScorecardId: String(item.employeeScorecardId?._id || item.employeeScorecardId?.id || item.employeeScorecardId || ''),
      employeeId: String(item.employeeId?._id || item.employeeId?.id || item.employeeId || ''),
      supervisorId: item.supervisorId ? String(item.supervisorId?._id || item.supervisorId?.id || item.supervisorId || '') : undefined,
    }));
  },

  getById: async (id: string): Promise<PerformanceAssessment> => {
    const response = await api.get<any>(`${API_BASE}/performance-assessments/${id}`);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      employeeScorecardId: String(normalized.employeeScorecardId?._id || normalized.employeeScorecardId?.id || normalized.employeeScorecardId || ''),
      employeeId: String(normalized.employeeId?._id || normalized.employeeId?.id || normalized.employeeId || ''),
      supervisorId: normalized.supervisorId ? String(normalized.supervisorId?._id || normalized.supervisorId?.id || normalized.supervisorId || '') : undefined,
    };
  },

  create: async (data: CreatePerformanceAssessmentDTO): Promise<PerformanceAssessment> => {
    const response = await api.post<any>(`${API_BASE}/performance-assessments`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item);
    return {
      ...normalized,
      employeeScorecardId: String(normalized.employeeScorecardId?._id || normalized.employeeScorecardId?.id || normalized.employeeScorecardId || ''),
      employeeId: String(normalized.employeeId?._id || normalized.employeeId?.id || normalized.employeeId || ''),
      supervisorId: normalized.supervisorId ? String(normalized.supervisorId?._id || normalized.supervisorId?.id || normalized.supervisorId || '') : undefined,
    };
  },

  update: async (id: string, data: UpdatePerformanceAssessmentDTO): Promise<PerformanceAssessment> => {
    const response = await api.put<any>(`${API_BASE}/performance-assessments/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/performance-assessments/${id}`);
  },

  sendReminder: async (id: string): Promise<void> => {
    await api.post(`${API_BASE}/performance-assessments/${id}/send-reminder`);
  },
};

// =============================================================================
// KPI ASSESSMENTS API
// =============================================================================

export const kpiAssessmentsAPI = {
  getByAssessment: async (assessmentId: string): Promise<KPIAssessment[]> => {
    const response = await api.get<any>(`${API_BASE}/performance-assessments/${assessmentId}/kpi-assessments`);
    const items = extractArrayData<any>(response);
    // Normalize IDs and handle populated ratingId/negotiatedRatingId objects
    // IMPORTANT: Preserve all fields including actualAchieved, evidence, etc.
    return normalizeIds(items).map((item: any) => ({
      ...item,
      // Explicitly preserve all assessment fields - handle both camelCase and snake_case, preserve empty strings
      actualAchieved: item.actualAchieved !== undefined && item.actualAchieved !== null 
        ? String(item.actualAchieved) 
        : (item.actual_achieved !== undefined && item.actual_achieved !== null 
          ? String(item.actual_achieved) 
          : ''),
      evidence: item.evidence !== undefined && item.evidence !== null ? String(item.evidence) : '',
      target: item.target !== undefined && item.target !== null ? String(item.target) : '',
      attachments: (item.attachments || []).filter((att: any) => 
        att.url && !att.url.startsWith('blob:') // Filter out blob URLs
      ),
      // Normalize rating IDs
      ratingId: item.ratingId?._id ? String(item.ratingId._id) : (item.ratingId ? String(item.ratingId) : null),
      negotiatedRatingId: item.negotiatedRatingId?._id ? String(item.negotiatedRatingId._id) : (item.negotiatedRatingId ? String(item.negotiatedRatingId) : null),
    })) as KPIAssessment[];
  },

  create: async (assessmentId: string, data: CreateKPIAssessmentDTO): Promise<KPIAssessment> => {
    const response = await api.post<any>(`${API_BASE}/performance-assessments/${assessmentId}/kpi-assessments`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item) as any;
    // Normalize populated ratingId/negotiatedRatingId objects
    return {
      ...normalized,
      ratingId: normalized.ratingId?._id ? String(normalized.ratingId._id) : (normalized.ratingId ? String(normalized.ratingId) : null),
      negotiatedRatingId: normalized.negotiatedRatingId?._id ? String(normalized.negotiatedRatingId._id) : (normalized.negotiatedRatingId ? String(normalized.negotiatedRatingId) : null),
    } as KPIAssessment;
  },

  update: async (id: string, data: UpdateKPIAssessmentDTO): Promise<KPIAssessment> => {
    const response = await api.put<any>(`${API_BASE}/kpi-assessments/${id}`, data);
    const item = extractData<any>(response);
    const normalized = normalizeId(item) as any;
    // Normalize populated ratingId/negotiatedRatingId objects
    return {
      ...normalized,
      ratingId: normalized.ratingId?._id ? String(normalized.ratingId._id) : (normalized.ratingId ? String(normalized.ratingId) : null),
      negotiatedRatingId: normalized.negotiatedRatingId?._id ? String(normalized.negotiatedRatingId._id) : (normalized.negotiatedRatingId ? String(normalized.negotiatedRatingId) : null),
      attachments: (normalized.attachments || []).filter((att: any) => 
        att.url && !att.url.startsWith('blob:') // Filter out blob URLs
      ),
    } as KPIAssessment;
  },

  uploadAttachment: async (file: File): Promise<Attachment> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post<any>(`${API_BASE}/kpi-assessments/upload-attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const item = extractData<any>(response);
    return {
      id: item.id || item._id,
      name: item.name,
      url: item.url,
      uploadedAt: item.uploadedAt || new Date().toISOString(),
    };
  },

  syncFromScorecard: async (assessmentId: string): Promise<{ data: KPIAssessment[]; syncedCount: number; updatedCount: number; deletedCount: number }> => {
    const response = await api.post<any>(`${API_BASE}/performance-assessments/${assessmentId}/sync-kpis`);
    const responseData = response.data || {};
    // Normalize IDs and handle populated ratingId/negotiatedRatingId objects
    const normalizedData = normalizeIds(responseData.data || []).map((item: any) => ({
      ...item,
      ratingId: item.ratingId?._id ? String(item.ratingId._id) : (item.ratingId ? String(item.ratingId) : null),
      negotiatedRatingId: item.negotiatedRatingId?._id ? String(item.negotiatedRatingId._id) : (item.negotiatedRatingId ? String(item.negotiatedRatingId) : null),
    })) as KPIAssessment[];
    return {
      data: normalizedData,
      syncedCount: responseData.syncedCount || 0,
      updatedCount: responseData.updatedCount || 0,
      deletedCount: responseData.deletedCount || 0,
    };
  },

  clearAll: async (assessmentId: string): Promise<{ deletedCount: number }> => {
    const response = await api.delete<any>(`${API_BASE}/performance-assessments/${assessmentId}/kpi-assessments`);
    const responseData = response.data || {};
    return {
      deletedCount: responseData.deletedCount || 0,
    };
  },
};

// =============================================================================
// DEVELOPMENT PLANS API
// =============================================================================

export const developmentPlansAPI = {
  getByAssessment: async (assessmentId: string): Promise<DevelopmentPlan[]> => {
    const response = await api.get<any>(`${API_BASE}/performance-assessments/${assessmentId}/development-plans`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  getAll: async (params?: { employeeId?: string; status?: string }): Promise<DevelopmentPlan[]> => {
    const queryParams = new URLSearchParams();
    if (params?.employeeId) queryParams.append('employeeId', params.employeeId);
    if (params?.status) queryParams.append('status', params.status);
    const response = await api.get<any>(`${API_BASE}/development-plans?${queryParams.toString()}`);
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  create: async (assessmentId: string, data: CreateDevelopmentPlanDTO): Promise<DevelopmentPlan> => {
    const response = await api.post<any>(`${API_BASE}/performance-assessments/${assessmentId}/development-plans`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  update: async (id: string, data: UpdateDevelopmentPlanDTO): Promise<DevelopmentPlan> => {
    const response = await api.put<any>(`${API_BASE}/development-plans/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/development-plans/${id}`);
  },

  uploadAttachment: async (planId: string, file: File): Promise<Attachment> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post<any>(`${API_BASE}/development-plans/${planId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const item = extractData<any>(response);
    return {
      id: item.id || item._id,
      name: item.name,
      url: item.url,
      uploadedAt: item.uploadedAt || new Date().toISOString(),
    };
  },

  deleteAttachment: async (attachmentId: string): Promise<void> => {
    await api.delete(`${API_BASE}/development-plans/attachments/${attachmentId}`);
  },
};

// =============================================================================
// ANNUAL DEVELOPMENT PLANS API
// =============================================================================

export interface AnnualDevelopmentPlan {
  id: string;
  scorecardId: string;
  employeeId: string;
  sourceType: 'development_plan' | 'competency' | 'feedback_dimension';
  sourceId: string;
  developmentArea: string;
  performanceAssessmentId: string;
  period: string;
  status: 'pending' | 'completed';
  trainingCost: number;
  createdAt?: string;
  updatedAt?: string;
}

export const annualDevelopmentPlansAPI = {
  getByScorecard: async (scorecardId: string): Promise<AnnualDevelopmentPlan[]> => {
    const response = await api.get<any>(`${API_BASE}/scorecards/${scorecardId}/annual-development-plans`);
    const items = extractArrayData<any>(response);
    // Normalize IDs and handle populated employeeId
    return normalizeIds(items).map((item: any) => ({
      ...item,
      employeeId: item.employeeId?._id 
        ? String(item.employeeId._id) 
        : (item.employeeId?.id 
          ? String(item.employeeId.id) 
          : String(item.employeeId || '')),
      // Preserve populated employee data if available
      employee: item.employeeId && typeof item.employeeId === 'object' ? item.employeeId : undefined,
    }));
  },

  create: async (scorecardId: string, items: Array<{
    employeeId: string;
    sourceType: 'development_plan' | 'competency' | 'feedback_dimension';
    sourceId: string;
    developmentArea: string;
    performanceAssessmentId: string;
    period: string;
  }>): Promise<AnnualDevelopmentPlan[]> => {
    const response = await api.post<any>(`${API_BASE}/scorecards/${scorecardId}/annual-development-plans`, { items });
    const itemsResult = extractArrayData<any>(response);
    return normalizeIds(itemsResult);
  },

  update: async (id: string, data: { trainingCost?: number; status?: 'pending' | 'completed' }): Promise<AnnualDevelopmentPlan> => {
    const response = await api.put<any>(`${API_BASE}/annual-development-plans/${id}`, data);
    const item = extractData<any>(response);
    return normalizeId(item);
  },

  bulkUpdateCost: async (scorecardId: string, ids: string[], cost: number): Promise<AnnualDevelopmentPlan[]> => {
    const response = await api.put<any>(`${API_BASE}/scorecards/${scorecardId}/annual-development-plans/bulk-cost`, { ids, cost });
    const items = extractArrayData<any>(response);
    return normalizeIds(items);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/annual-development-plans/${id}`);
  },
};

// Scorecard Events API
export const scorecardEventsAPI = {
  getAll: async (scorecardId: string): Promise<any[]> => {
    const response = await api.get(`${API_BASE}/scorecards/${scorecardId}/events`);
    return response.data.data;
  },

  create: async (scorecardId: string, data: {
    description: string;
    eventStartDate: string;
    eventEndDate: string;
    notificationDaysBefore: number;
    notificationRepeatDays: number;
    targetRoles: string[];
  }): Promise<any> => {
    const response = await api.post(`${API_BASE}/scorecards/${scorecardId}/events`, data);
    return response.data.data;
  },

  update: async (id: string, data: {
    description: string;
    eventStartDate: string;
    eventEndDate: string;
    notificationDaysBefore: number;
    notificationRepeatDays: number;
    targetRoles: string[];
  }): Promise<any> => {
    const response = await api.put(`${API_BASE}/scorecard-events/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/scorecard-events/${id}`);
  },

  sendNotification: async (id: string): Promise<any> => {
    const response = await api.post(`${API_BASE}/scorecard-events/${id}/send-notification`);
    return response.data.data;
  },
};

export default strategicPerformanceAPI;
